import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export function DeleteEntitiesDialog({ open, onOpenChange, orgs, onDone }) {
  const [password, setPassword] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPassword(""); setTyped(""); } }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/entities/bulk-delete", { org_ids: orgs.map((o) => o.id), password });
      toast.success(`${r.data.deleted} compan${r.data.deleted === 1 ? "y" : "ies"} deleted · restorable for 30 days`);
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="delete-entities-dialog">
        <DialogHeader>
          <DialogTitle>Delete {orgs.length} deactivated compan{orgs.length === 1 ? "y" : "ies"}?</DialogTitle>
          <DialogDescription>
            They disappear from the platform now. You can restore them from “Recently deleted” for 30 days;
            after that all their data is wiped permanently.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-sm max-h-32 overflow-y-auto list-disc pl-5" data-testid="delete-entities-list">
          {orgs.map((o) => <li key={o.id}>{o.name}</li>)}
        </ul>
        <div className="space-y-2">
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type DELETE to confirm" data-testid="delete-entities-confirm" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your super-admin password" data-testid="delete-entities-password" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="delete-entities-cancel">Cancel</Button>
          <Button onClick={run} disabled={busy || typed !== "DELETE" || !password} className="bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="delete-entities-submit">
            <Trash2 className="w-4 h-4" /> {busy ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeletedEntitiesPanel({ refreshKey, onRestored }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get("/admin/entities/deleted");
    setRows(r.data?.entities || []);
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const restore = async (o) => {
    if (!window.confirm(`Restore ${o.name}? It comes back deactivated — reactivate it to let users sign in.`)) return;
    try { await api.post(`/admin/entities/${o.id}/restore`); toast.success(`${o.name} restored (deactivated)`); load(); onRestored?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  if (!rows.length) return null;
  return (
    <Card className="rounded-sm bg-white border border-slate-200 overflow-hidden" data-testid="deleted-entities-panel">
      <div className="p-3 border-b border-slate-200 text-sm font-semibold text-slate-800">Recently deleted · restorable until the purge date</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-50">
          <tr><th className="text-left p-3">Company</th><th className="text-left p-3">Deleted</th><th className="text-left p-3">Permanently wiped on</th><th className="text-right p-3">Action</th></tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="border-t border-slate-100" data-testid={`deleted-row-${o.id}`}>
              <td className="p-3 font-medium">{o.name}<div className="text-[10px] font-mono text-slate-500">{o.gstin || "no GSTIN"}</div></td>
              <td className="p-3 text-xs text-slate-600">{o.deleted_at ? new Date(o.deleted_at).toLocaleString("en-IN") : "—"}{o.deleted_by ? ` · ${o.deleted_by}` : ""}</td>
              <td className="p-3 text-xs text-red-700">{o.hard_purge_at ? new Date(o.hard_purge_at).toLocaleDateString("en-IN") : "—"}</td>
              <td className="p-3 text-right">
                <Button size="sm" variant="outline" onClick={() => restore(o)} className="gap-1.5 rounded-sm" data-testid={`restore-${o.id}`}>
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
